import { lazy, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { themeChange } from 'theme-change';
import './App.css';
import initializeApp from './app/init';
import BbsCmsSessionGate from './components/BbsCms/BbsCmsSessionGate';
import { selectIsAuthenticated } from './features/common/authSlice';
import useServiceAccess from './hooks/useServiceAccess';
import './i18n';
import config from './OEMConfig';
import LoginByRoot from './pages/LoginByRoot';
import authStorage from './utils/authStorage';

const Layout = lazy(() => import('./containers/Layout'));
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Register = lazy(() => import('./pages/Register'));
const Documentation = lazy(() => import('./pages/Documentation'));

import 'react-datepicker/dist/react-datepicker.css';
import LanguageSync from './components/Common/LanguageSync';

// Initializing different libraries
initializeApp();

// Check for login and initialize axios

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { refreshAccess } = useServiceAccess();
  const location = useLocation();
  const isFramed = window.self !== window.top;

  const updateFavicon = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = reader.result; // Base64 string

        // Remove existing favicon
        document.querySelectorAll("link[rel='icon']").forEach((e) => e.remove());

        // Create a new favicon link
        const link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        link.href = base64Data;

        document.head.appendChild(link);
      };

      reader.readAsDataURL(blob); // Convert Blob to Base64
    } catch (error) {
      console.error('Error updating favicon:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshAccess({ force: true });
    }
  }, [isAuthenticated, refreshAccess]);

  useEffect(() => {
    const oemConfig = config();
    if (oemConfig.favicon_path) {
      updateFavicon(oemConfig?.favicon_path);
    }
  }, [updateFavicon]);

  useEffect(() => {
    themeChange(false);
  }, []);

  useEffect(() => {
    const isSuper = authStorage.get('is_super_login') === 'true';
    if (isSuper) {
      document.documentElement.setAttribute('data-theme', 'super_theme');
      document.body.classList.add('super-user');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
      document.body.classList.remove('super-user');
    }
  }, [location]);

  return (
    <>
      <LanguageSync />
      <Routes>
        {/* If already authenticated, prevent access to login */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/app/dashboard" /> : isFramed ? <BbsCmsSessionGate /> : <Login />}
        />
        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/app/dashboard" /> : <ResetPassword />}
        />

        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/app/dashboard" /> : <ForgotPassword />}
        />

        <Route path="/documentation" element={<Documentation />} />

        <Route path="/login-by-root" element={<LoginByRoot />} />

        {/* Protected routes */}
        <Route
          path="/app/*"
          element={isAuthenticated ? <Layout /> : isFramed ? <BbsCmsSessionGate /> : <Navigate to="/login" />}
        />

        {/* Fallback route */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/app/dashboard" replace />
            ) : isFramed ? (
              <BbsCmsSessionGate />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </>
  );
}

export default App;
